/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ComponentFactoryResolver,
  ComponentRef,
  Injectable,
  TemplateRef,
  Type,
  ViewContainerRef,
} from '@angular/core';

import { TemplateSiteComponent } from '@gh/core-ui';

import { ICellRenderer, ICellRendererComp } from 'ag-grid-community';

import { GridComponentFactory } from './grid-component-factory';
import { GridCellRendererComponent } from './grid-component.interfaces';

@Injectable()
// tslint:disable-next-line:max-line-length
export class GridCellRendererFactory implements GridComponentFactory<Type<GridCellRendererComponent> | TemplateRef<any>, Type<ICellRenderer>> {
  constructor(private componentFactoryResolver: ComponentFactoryResolver) {

  }

  createComponent(componentType: Type<GridCellRendererComponent> | TemplateRef<any>,
                  viewContainerRef: ViewContainerRef): Type<ICellRendererComp> {
    if (componentType instanceof TemplateRef) {
      return this.createComponentFromTemplate(componentType, viewContainerRef);
    } else {
      return this.createComponentByFactory(componentType, viewContainerRef);
    }
  }

  createComponentFromTemplate(templateRef: TemplateRef<any>,
                              viewContainerRef: ViewContainerRef): Type<ICellRendererComp> {
    const that = this;

    class TemplateCellRenderer implements ICellRenderer {
      private componentRef: ComponentRef<TemplateSiteComponent<any>>;
      private componentInstance: TemplateSiteComponent<any>;

      init(params: any): void {
        const factory = that.componentFactoryResolver.resolveComponentFactory(TemplateSiteComponent);
        this.componentRef = viewContainerRef.createComponent(factory);
        this.componentInstance = this.componentRef.instance;
        this.componentInstance.setTemplateRef(templateRef);
        this.attachParameters(params);
      }

      destroy(): void {
        this.componentRef.destroy();
      }

      refresh(params: any): boolean {
        this.attachParameters(params);
        return true;
      }

      getGui(): HTMLElement {
        return this.componentRef.location.nativeElement;
      }

      private attachParameters(params: any): void {
        this.componentInstance.setContext({
          $implicit: params.data,
          colDef: params.colDef,
          column: params.column,
          node: params.node,
        });
      }
    }

    return TemplateCellRenderer;
  }

  createComponentByFactory(componentType: Type<GridCellRendererComponent>,
                           viewContainerRef: ViewContainerRef): Type<ICellRendererComp> {
    const that = this;

    class ComponentCellRenderer implements ICellRenderer {
      private componentRef: ComponentRef<GridCellRendererComponent>;
      private componentInstance: GridCellRendererComponent;

      init(params: any): void {
        const factory = that.componentFactoryResolver.resolveComponentFactory(componentType);
        this.componentRef = viewContainerRef.createComponent(factory);
        this.componentInstance = this.componentRef.instance;
        this.componentInstance.agInit(params);
        this.componentRef.changeDetectorRef.detectChanges();
      }

      destroy(): void {
        this.componentRef.destroy();
      }

      refresh(params: any): boolean {
        if (this.componentInstance.refresh) {
          return this.componentInstance.refresh(params);
        } else {
          throw new Error(`Component '${componentType.name}' does not have refresh method`);
        }
      }

      getGui(): HTMLElement {
        return this.componentRef.location.nativeElement;
      }
    }

    return ComponentCellRenderer;
  }
}
